import React, {useEffect, useState} from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import logo from './images/logo.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Users from "../../../graphql/queries/Users";
import UserUpdateLocale from "../../../graphql/mutations/UserUpdateLocale";

const NavigationBar = () => {
  const [locale, setLocale] = useState('ua');
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const categoryMatch = location.pathname.match(/\/catalogue\/([^/]+)/);
  const currentCategory = categoryMatch ? categoryMatch[1] : "";

  const handleLanguageSwitch = () => {
    const newLang = locale === 'en' ? 'ua' : 'en';
    i18n.changeLanguage(newLang);
    setLocale(newLang);

    if (!token) return;

    UserUpdateLocale.commit({ locale: newLang.toUpperCase() }).then(({ userUpdateLocale }) => {
      const { user: { locale } } = userUpdateLocale || {};

      setLocale(locale.toLowerCase());
      i18n.changeLanguage(locale.toLowerCase());
    });
  };

  const handleCategoryChange = (event) => {
    const selectedCategory = event.target.value;
    navigate(`/catalogue/${selectedCategory}`);
  };

  const menuItems = [
    { label: t('home'), to: '/' },
    { label: t('fullCollection'), value: 'full-collection', icon: <LibraryBooksOutlinedIcon fontSize="small" /> },
    { label: t('kids'), value: 'kids', icon: <ChildCareOutlinedIcon fontSize="small" /> },
    { label: t('adult'), value: 'adult', icon: <MenuBookOutlinedIcon fontSize="small" /> },
    { label: t('english'), value: 'english', icon: <TranslateOutlinedIcon fontSize="small" /> },
    { label: t('myHoldings'), value: 'my-holdings', auth: true, icon: <Inventory2OutlinedIcon fontSize="small" /> },
    { label: t('myReservations'), value: 'my-reservations', auth: true, icon: <EventAvailableOutlinedIcon fontSize="small" /> },
    { label: t('myReadings'), value: 'my-readings', auth: true, icon: <HistoryEduOutlinedIcon fontSize="small" /> }
  ];


  useEffect(() => {
    if (!token) return;

    Users
      .fetch()
      .then((data) => {
        const user = data.me;
        setLocale(user.locale.toLowerCase());
        i18n.changeLanguage(user.locale.toLowerCase());
      })
      .catch((error) => {
        console.error('Error fetching user profile:', error);
      });
  }, []);

  const renderDesktopNav = () => (
    <Box display="flex" alignItems="center" width="100%">
      <Typography variant="h6" sx={{ flexGrow: 1, lineHeight: 1 }}>
        <Link to="/" style={{ color: 'white', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Box
            component="img"
            src={logo}
            alt="L’Українка"
            sx={{ height: 45, width: 'auto', display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
          />
          <Box component="span" sx={{ display: 'inline-block', verticalAlign: 'middle', lineHeight: 1 }}>
            {t('library')}
          </Box>
        </Link>
      </Typography>
      <FormControl
        variant="outlined"
        size="small"
        sx={{
          '& .MuiInputBase-root': { color: 'white' },
          '& .MuiSvgIcon-root': { color: 'white' }
        }}
      >
        {/*<InputLabel id="catalogue-label" shrink sx={{ color: 'white' }}>*/}
        {/*  {t('categories')}*/}
        {/*</InputLabel>*/}
        <Select
          labelId="catalogue-label"
          // label={t('categories')}
          value={currentCategory || ""}
          onChange={handleCategoryChange}
          displayEmpty
          renderValue={(value) => {
            const item = menuItems.find((it) => it.value === value);
            if (!item) {
              return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, lineHeight: 1 }}>
                  <CategoryOutlinedIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 18 }} />
                  <span style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1 }}>
                    {t('selectCategory', t('categories'))}
                  </span>
                </Box>
              );
            }
            return (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, lineHeight: 1 }}>
                <Box sx={{ display: 'inline-flex', lineHeight: 1 }}>
                  {React.cloneElement(item.icon, { sx: { color: 'white', fontSize: 18, verticalAlign: 'middle' } })}
                </Box>
                <span style={{ color: 'white', lineHeight: 1 }}>{item.label}</span>
              </Box>
            );
          }}
          sx={{
            minWidth: 'inherit',
            // Keep inline alignment tidy
            '& .MuiSelect-select': {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              lineHeight: 1,
              paddingTop: '6px',
              paddingBottom: '6px'
            },
            // Remove the outlined border in all states
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent !important'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent !important'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent !important'
            },
            // Avoid blue/grey fill on focus in some browsers
            '& .MuiSelect-select:focus': {
              backgroundColor: 'transparent'
            },
            // Remove any default focus outline ring
            '&:focus': { outline: 'none' }
          }}
        >
          <MenuItem value="" disabled>{t('categories')}</MenuItem>
          {menuItems.map(
            item =>
              item.value &&
              (!item.auth || token) && (
                <MenuItem key={item.value} value={item.value}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </MenuItem>
              )
          )}
        </Select>
      </FormControl>
      {token ? (
        <>
          <Button color="inherit" component={Link} to="/account/checkout" startIcon={<AccountCircleOutlinedIcon />}>
            {t('account')}
          </Button>
          <Button color="inherit" startIcon={<LogoutOutlinedIcon />} onClick={() => {
            window.localStorage.clear();
            window.location.href = '/sign-in';
          }}>
            {t('logout')}
          </Button>
        </>
      ) : (
        <>
          <Button color="inherit" component={Link} to="/sign-in" startIcon={<LoginOutlinedIcon />}>
            {t('signin')}
          </Button>
          <Button color="inherit" component={Link} to="/sign-up" startIcon={<HowToRegOutlinedIcon />}>
            {t('signup')}
          </Button>
        </>
      )}
      <IconButton color="inherit" onClick={handleLanguageSwitch}>
        <LanguageIcon />
      </IconButton>
    </Box>
  );

  const renderMobileNav = () => (
    <>
      <IconButton
        color="inherit"
        edge="start"
        onClick={() => setDrawerOpen(true)}
        sx={{ mr: 2 }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250 }} role="presentation">
          <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/" selected={location.pathname === '/'} onClick={() => setDrawerOpen(false)}>
                <ListItemIcon>
                  <HomeOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={t('home')} />
              </ListItemButton>
            </ListItem>
            {menuItems.map(
              item =>
                item.value &&
                (!item.auth || token) && (
                  <ListItem key={item.value} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={`/catalogue/${item.value}`}
                      selected={location.pathname.startsWith(`/catalogue/${item.value}`)}
                      onClick={() => setDrawerOpen(false)}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                )
            )}
            {token ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/account/checkout" selected={location.pathname.startsWith('/account')} onClick={() => setDrawerOpen(false)}>
                    <ListItemIcon>
                      <AccountCircleOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('account')} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      window.localStorage.clear();
                      window.location.href = '/sign-in';
                    }}
                  >
                    <ListItemIcon>
                      <LogoutOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('logout')} />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/sign-in" selected={location.pathname.startsWith('/sign-in')} onClick={() => setDrawerOpen(false)}>
                    <ListItemIcon>
                      <LoginOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('signin')} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/sign-up" selected={location.pathname.startsWith('/sign-up')} onClick={() => setDrawerOpen(false)}>
                    <ListItemIcon>
                      <HowToRegOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('signup')} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
      <Typography
        variant="h6"
        sx={{
          flexGrow: 1,
          fontSize: { xs: '1rem', sm: '1.25rem' },
        }}
      >
        {t('library')}
      </Typography>
      <IconButton color="inherit" onClick={handleLanguageSwitch}>
        <LanguageIcon />
      </IconButton>
    </>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'linear-gradient(315deg, #80a748 0%, #a2c857 50%, #5e7d2a 100%)',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
      }}
    >
      <Toolbar>
        {isMobile ? renderMobileNav() : renderDesktopNav()}
      </Toolbar>
    </AppBar>
  );
};

export default NavigationBar;
